import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { TENANTS, SYNC_RUNS } from '@/data/mock';
import { useTenants, useSyncRuns, useTenantEntitlement } from '@/lib/use-admin-data';
import { cn } from '@/lib/utils';
import { tenantsApi, midigatorAuthApi, apiErrorParts } from '@/lib/api';
import type { MidigatorUrlKind } from '@/lib/api';
import { requireTokenLock } from '@/lib/midigator-lock';
import type { FieldSchemaEntry } from '@/lib/api';
import { TenantAccessPanel, TenantAccessChip } from '@/components/tenants/TenantAccessPanel';
import { TenantMcpPanel } from '@/components/tenants/TenantMcpPanel';
import { InviteFirstUserPanel } from '@/components/tenants/InviteFirstUserPanel';
import { TenantMembersPanel } from '@/components/tenants/TenantMembersPanel';
import { EvidenceProfilePanel } from '@/components/tenants/EvidenceProfilePanel';
import { BrandsPanel } from '@/components/tenants/BrandsPanel';
import {
  ToggleRight,
  ToggleLeft,
  Sliders,
  CircleNotch,
  WarningCircle,
  Gavel,
  Timer,
  Crosshair,
  Clock,
  BellRinging,
  ArrowsSplit,
  ListChecks,
  ListPlus,
  X,
  ArrowClockwise,
  ArrowsClockwise,
  WebhooksLogo,
  Lock,
  Table,
  MagnifyingGlass,
  CaretDown,
  CaretRight,
} from '@phosphor-icons/react';

const PLATFORM_BADGES: Record<string, string> = {
  chargebee: 'Chargebee',
  recurly: 'Recurly',
  braintree: 'Braintree',
  paypal: 'PayPal',
  checkout_com: 'Checkout.com',
};

type Tab = 'access' | 'credentials' | 'brands' | 'data_preview' | 'sync_history' | 'action_settings';

// ---------------------------------------------------------------------------
// Action Settings types (mirrors shared/types/routing.ts for the admin UI)
// ---------------------------------------------------------------------------

interface AlertRuleConditions {
  alertSources?: string[];
  eventTypes?: string[];
  amountMin?: number;
  amountMax?: number;
  reasonCodes?: string[];
  currencies?: string[];
}

interface AlertRuleAction {
  type: 'refund' | 'cancel' | 'refund_and_cancel' | 'notate_only' | 'decline';
  declineCode?: string;
  flagCustomer?: boolean;
}

interface AlertRule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  conditions: AlertRuleConditions;
  action: AlertRuleAction;
}

interface FieldProfileTriggerConfig {
  fields: Record<string, string>;
  addNote: boolean;
}

interface PlatformFieldProfile {
  triggers: Record<string, FieldProfileTriggerConfig>;
}

type FieldProfiles = Record<string, PlatformFieldProfile>;

interface ActionSettingsData {
  autoEnrich: boolean;
  autoPush: boolean;
  autoBillingActions: boolean;
  useAdkEnrichment: boolean;
  alertActionsEnabled: boolean;
  kountAutoRespond?: boolean;
  alertFallbackAction?: 'resolve_refund' | 'resolve_cancel' | 'decline' | 'notate_only' | 'use_toggles';
  alertRules?: AlertRule[];
  alertActions?: { cancel: boolean; refund: boolean; notate: boolean; updateCustomer: boolean };
  kombatixPdf?: { enabled: boolean; minScore: number };
  zendeskPdf?: { enabled: boolean };
  fieldProfiles?: FieldProfiles;
  sla?: { targetPct?: number; graceHours?: number };
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Reusable toggle component (admin design system)
// ---------------------------------------------------------------------------

function AdminToggle({
  enabled,
  onChange,
  label,
  disabled,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={cn(
        'flex items-center gap-3 w-full text-left px-3 py-2 rounded-lg transition',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer',
      )}
    >
      {enabled ? (
        <ToggleRight size={20} weight="regular" className="text-primary" />
      ) : (
        <ToggleLeft size={20} weight="regular" className="text-slate-300" />
      )}
      <span className="text-sm text-slate-700">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Action Settings tab content
// ---------------------------------------------------------------------------

const FALLBACK_OPTIONS: { value: string; label: string }[] = [
  { value: 'use_toggles', label: 'Use alert action toggles' },
  { value: 'resolve_refund', label: 'Resolve + Refund' },
  { value: 'resolve_cancel', label: 'Resolve + Cancel' },
  { value: 'decline', label: 'Decline (proceed to chargeback)' },
  { value: 'notate_only', label: 'Notate only' },
];

const ACTION_TYPE_BADGES: Record<string, { label: string; color: string }> = {
  refund: { label: 'Refund', color: 'bg-green-50 text-green-700 border-green-100' },
  cancel: { label: 'Cancel', color: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
  refund_and_cancel: { label: 'Refund + Cancel', color: 'bg-blue-50 text-blue-700 border-blue-100' },
  notate_only: { label: 'Notate', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  decline: { label: 'Decline', color: 'bg-red-50 text-red-700 border-red-100' },
};

// ---------------------------------------------------------------------------
// Field Profiles section
// ---------------------------------------------------------------------------

const TRIGGER_LABELS: Record<string, { label: string; category: string }> = {
  alert_refund_cancel: { label: 'Alert — Refund + Cancel', category: 'Alert' },
  alert_rdr_accepted: { label: 'Alert — RDR Accepted', category: 'Alert' },
  alert_rdr_declined: { label: 'Alert — RDR Declined', category: 'Alert' },
  alert_cancel_only: { label: 'Alert — Cancel Only', category: 'Alert' },
  alert_refund_only: { label: 'Alert — Refund Only', category: 'Alert' },
  alert_decline: { label: 'Alert — Decline', category: 'Alert' },
  alert_notate_only: { label: 'Alert — Notate Only', category: 'Alert' },
  chargeback_actions: { label: 'Chargeback — Billing Cleanup', category: 'Chargeback' },
  manual_cancel: { label: 'Manual — Cancel', category: 'Manual' },
  manual_refund: { label: 'Manual — Refund', category: 'Manual' },
  manual_review: { label: 'Manual — Review', category: 'Manual' },
  outcome_won: { label: 'Outcome — Won', category: 'Outcome' },
  outcome_lost: { label: 'Outcome — Lost', category: 'Outcome' },
  outcome_withdrawn: { label: 'Outcome — Withdrawn', category: 'Outcome' },
  outcome_prearb: { label: 'Outcome — Pre-Arb', category: 'Outcome' },
};

const ALL_TRIGGERS = Object.keys(TRIGGER_LABELS);

const TEMPLATE_VARIABLES = [
  { key: 'kountEventType', label: 'Kount Event Type' },
  { key: 'outcome', label: 'Outcome (Won/Lost/Pre-Arb)' },
  { key: 'caseId', label: 'Case ID' },
  { key: 'processor', label: 'Processor' },
  { key: 'alertSource', label: 'Alert Source' },
  { key: 'reasonCode', label: 'Reason Code' },
  { key: 'transactionId', label: 'Transaction ID' },
];

const PLATFORM_NAMES: Record<string, string> = {
  chargebee: 'Chargebee',
  recurly: 'Recurly',
  stripe: 'Stripe',
  sticky_io: 'Sticky.io',
  braintree: 'Braintree',
  revolv3: 'Revolv3',
};

function FieldProfilesSection({
  settings,
  onSave,
  isSaving,
  tenantId,
}: {
  settings: ActionSettingsData;
  onSave: (patch: Record<string, unknown>) => void;
  isSaving: boolean;
  tenantId: string;
}) {
  const profiles = settings.fieldProfiles ?? {};
  const platforms = Object.keys(profiles);
  const [selectedPlatform, setSelectedPlatform] = useState(platforms[0] ?? 'chargebee');
  const [expandedTrigger, setExpandedTrigger] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ trigger: string; key: string; value: string } | null>(null);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [fieldSchema, setFieldSchema] = useState<{ platform: string; fields: FieldSchemaEntry[]; fetchedAt: number } | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (selectedPlatform) {
      tenantsApi.getCachedFieldSchema(tenantId, selectedPlatform).then(setFieldSchema).catch(() => setFieldSchema(null));
    }
  }, [selectedPlatform, tenantId]);

  const platformProfile = profiles[selectedPlatform]?.triggers ?? {};

  function saveTrigger(trigger: string, config: FieldProfileTriggerConfig) {
    const updated: FieldProfiles = {
      ...profiles,
      [selectedPlatform]: {
        triggers: {
          ...(profiles[selectedPlatform]?.triggers ?? {}),
          [trigger]: config,
        },
      },
    };
    onSave({ fieldProfiles: updated });
  }

  function removeField(trigger: string, fieldKey: string) {
    const current = platformProfile[trigger];
    if (!current) return;
    const { [fieldKey]: _, ...rest } = current.fields;
    saveTrigger(trigger, { ...current, fields: rest });
  }

  function addField(trigger: string) {
    if (!newFieldKey.trim()) return;
    const current = platformProfile[trigger] ?? { fields: {}, addNote: true };
    saveTrigger(trigger, {
      ...current,
      fields: { ...current.fields, [newFieldKey.trim()]: newFieldValue.trim() },
    });
    setNewFieldKey('');
    setNewFieldValue('');
  }

  function toggleNote(trigger: string) {
    const current = platformProfile[trigger] ?? { fields: {}, addNote: true };
    saveTrigger(trigger, { ...current, addNote: !current.addNote });
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sliders size={20} weight="regular" className="text-primary" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Field Profiles</h3>
        </div>
        {/* Platform selector + Sync Fields */}
        <div className="flex items-center gap-3">
          <select
            value={selectedPlatform}
            onChange={(e) => { setSelectedPlatform(e.target.value); setExpandedTrigger(null); }}
            className="text-xs border border-slate-200 rounded px-2 py-1 text-slate-600"
          >
            {Object.entries(PLATFORM_NAMES).map(([key, name]) => (
              <option key={key} value={key}>{name}</option>
            ))}
          </select>
          <button
            onClick={async () => {
              setSyncing(true);
              try {
                const res = await tenantsApi.syncFieldSchema(tenantId, selectedPlatform);
                if (res.data) setFieldSchema(res.data);
              } catch (err) {
                console.error('Failed to sync field schema', err);
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
          >
            {syncing ? (
              <CircleNotch size={16} weight="regular" className="animate-spin" />
            ) : (
              <ArrowsClockwise size={16} weight="regular" />
            )}
            {syncing ? 'Syncing...' : 'Sync Fields'}
          </button>
          {fieldSchema && (
            <span className="text-xs text-slate-400">
              Last synced: {new Date(fieldSchema.fetchedAt * 1000).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {ALL_TRIGGERS.map((trigger) => {
          const config = platformProfile[trigger];
          const isDefault = !config;
          const fields = config?.fields ?? {};
          const fieldCount = Object.keys(fields).length;
          const addNote = config?.addNote ?? false;
          const isExpanded = expandedTrigger === trigger;
          const info = TRIGGER_LABELS[trigger];

          return (
            <div key={trigger}>
              {/* Trigger header */}
              <button
                onClick={() => setExpandedTrigger(isExpanded ? null : trigger)}
                className="w-full px-6 py-3 flex items-center gap-3 hover:bg-slate-50 transition text-left"
              >
                {isExpanded ? (
                  <CaretDown size={16} weight="regular" className="text-slate-400" />
                ) : (
                  <CaretRight size={16} weight="regular" className="text-slate-400" />
                )}
                <span className="text-sm text-slate-700 flex-1">{info.label}</span>
                {isDefault ? (
                  <span className="text-xs text-slate-400 italic">Using defaults</span>
                ) : (
                  <span className="text-xs text-slate-500">
                    {fieldCount} field{fieldCount !== 1 ? 's' : ''} {addNote ? '+ note' : ''}
                  </span>
                )}
                <span className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded font-medium',
                  info.category === 'Alert' ? 'bg-blue-50 text-blue-600'
                  : info.category === 'Chargeback' ? 'bg-amber-50 text-amber-600'
                  : info.category === 'Outcome' ? 'bg-green-100 text-green-800'
                  : 'bg-slate-50 text-slate-500',
                )}>
                  {info.category}
                </span>
              </button>

              {/* Expanded: field editor */}
              {isExpanded && (
                <div className="px-6 pb-4 space-y-3">
                  {/* Note toggle */}
                  <div className="flex items-center gap-2">
                    <AdminToggle
                      enabled={config?.addNote ?? true}
                      onChange={() => toggleNote(trigger)}
                      label="Add outcome-aware note"
                      disabled={isSaving}
                    />
                  </div>

                  {/* Existing fields */}
                  {Object.entries(fields).length > 0 ? (
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                      {Object.entries(fields).map(([key, value]) => {
                        const fieldDef = fieldSchema?.fields?.find(f => f.key === key);
                        const isEditing = editingField?.trigger === trigger && editingField?.key === key;

                        return (
                          <div key={key} className="flex items-center gap-2">
                            <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-600 min-w-[180px]">{key}</code>
                            <span className="text-xs text-slate-400">=</span>
                            {isEditing ? (
                              fieldDef?.type === 'dropdown' && fieldDef.options?.length ? (
                                <select
                                  value={editingField.value}
                                  onChange={(e) => {
                                    const current = platformProfile[trigger] ?? { fields: {}, addNote: true };
                                    saveTrigger(trigger, { ...current, fields: { ...current.fields, [key]: e.target.value } });
                                    setEditingField(null);
                                  }}
                                  onBlur={() => setEditingField(null)}
                                  className="text-xs border border-primary rounded px-2 py-1 text-slate-700 flex-1 bg-white"
                                  autoFocus
                                >
                                  <option value="">Select value...</option>
                                  {fieldDef.options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex items-center gap-1 flex-1">
                                  <input
                                    type="text"
                                    value={editingField.value}
                                    onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                                    onBlur={() => {
                                      const current = platformProfile[trigger] ?? { fields: {}, addNote: true };
                                      saveTrigger(trigger, { ...current, fields: { ...current.fields, [key]: editingField.value } });
                                      setEditingField(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                      if (e.key === 'Escape') setEditingField(null);
                                    }}
                                    className="text-xs border border-primary rounded px-2 py-1 text-slate-700 flex-1"
                                    autoFocus
                                  />
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        setEditingField({ ...editingField, value: editingField.value + `{{${e.target.value}}}` });
                                        e.target.value = '';
                                      }
                                    }}
                                    className="text-xs border border-slate-200 rounded px-1 py-1 text-slate-400 bg-white"
                                  >
                                    <option value="">+ var</option>
                                    {TEMPLATE_VARIABLES.map(v => (
                                      <option key={v.key} value={v.key}>{v.label}</option>
                                    ))}
                                  </select>
                                </div>
                              )
                            ) : (
                              <button
                                onClick={() => setEditingField({ trigger, key, value: value as string })}
                                className="text-xs text-slate-700 hover:text-primary cursor-pointer flex-1 text-left truncate"
                              >
                                {value as string}
                              </button>
                            )}
                            <button
                              onClick={() => removeField(trigger, key)}
                              disabled={isSaving}
                              className="text-slate-400 hover:text-red-500 transition"
                            >
                              <X size={16} weight="regular" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic px-1">No field updates configured for this trigger.</p>
                  )}

                  {/* Add field */}
                  <div className="flex items-center gap-2">
                    {fieldSchema?.fields?.length ? (
                      <select
                        value={newFieldKey}
                        onChange={(e) => setNewFieldKey(e.target.value)}
                        className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 w-[200px] bg-white"
                      >
                        <option value="">Select field...</option>
                        {fieldSchema.fields.map(f => (
                          <option key={f.key} value={f.key}>{f.label} ({f.key})</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="Field name (e.g. cf_customer_status)"
                        value={newFieldKey}
                        onChange={(e) => setNewFieldKey(e.target.value)}
                        className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 w-[200px]"
                      />
                    )}
                    {(() => {
                      const newFieldDef = fieldSchema?.fields?.find(f => f.key === newFieldKey);
                      if (newFieldDef?.type === 'dropdown' && newFieldDef.options?.length) {
                        return (
                          <select
                            value={newFieldValue}
                            onChange={(e) => setNewFieldValue(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 flex-1 bg-white"
                          >
                            <option value="">Select value...</option>
                            {newFieldDef.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        );
                      }
                      return (
                        <div className="flex items-center gap-1 flex-1">
                          <input
                            type="text"
                            placeholder="Value"
                            value={newFieldValue}
                            onChange={(e) => setNewFieldValue(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 flex-1"
                          />
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                setNewFieldValue(newFieldValue + `{{${e.target.value}}}`);
                                e.target.value = '';
                              }
                            }}
                            className="text-xs border border-slate-200 rounded px-1 py-1.5 text-slate-400 bg-white"
                          >
                            <option value="">+ var</option>
                            {TEMPLATE_VARIABLES.map(v => (
                              <option key={v.key} value={v.key}>{v.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })()}
                    <button
                      onClick={() => addField(trigger)}
                      disabled={isSaving || !newFieldKey.trim()}
                      className={cn(
                        'text-xs px-3 py-1.5 rounded font-medium transition',
                        newFieldKey.trim()
                          ? 'bg-primary text-white hover:bg-primary/90'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed',
                      )}
                    >
                      Add
                    </button>
                  </div>

                  {/* Customize from defaults button */}
                  {isDefault && (
                    <button
                      onClick={() => {
                        // Copy built-in defaults for this trigger (Chargebee only for now)
                        const CHARGEBEE_DEFAULTS: Record<string, FieldProfileTriggerConfig> = {
                          alert_refund_cancel: { fields: { fraud_flag: 'fraudulent', auto_collection: 'off', cf_customer_status: 'Disabled', cf_update_reason: 'Kount DCM Cancellation' }, addNote: true },
                          alert_rdr_accepted: { fields: { cf_customer_status: 'Disabled', cf_update_reason: 'RDR Accepted' }, addNote: true },
                          alert_rdr_declined: { fields: {}, addNote: true },
                          alert_cancel_only: { fields: { fraud_flag: 'fraudulent', auto_collection: 'off', cf_customer_status: 'Disabled', cf_update_reason: 'Kount DCM Cancellation' }, addNote: true },
                          alert_refund_only: { fields: {}, addNote: true },
                          alert_notate_only: { fields: {}, addNote: true },
                          alert_decline: { fields: {}, addNote: true },
                          chargeback_actions: { fields: { fraud_flag: 'fraudulent', auto_collection: 'off', cf_customer_status: 'Disabled', cf_rejection_detail: 'Chargeback' }, addNote: true },
                          manual_cancel: { fields: { cf_customer_status: 'Disabled', cf_update_reason: 'Manual Cancellation' }, addNote: true },
                          manual_refund: { fields: {}, addNote: true },
                          manual_review: { fields: {}, addNote: true },
                        };
                        if (selectedPlatform === 'chargebee' && CHARGEBEE_DEFAULTS[trigger]) {
                          saveTrigger(trigger, CHARGEBEE_DEFAULTS[trigger]);
                        } else {
                          saveTrigger(trigger, { fields: {}, addNote: true });
                        }
                      }}
                      disabled={isSaving}
                      className="text-xs text-primary hover:underline"
                    >
                      Customize (copy defaults)
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action Settings tab content
// ---------------------------------------------------------------------------

function ActionSettingsTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();

  const {
    data: settings,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'action-settings'],
    queryFn: async (): Promise<ActionSettingsData> => {
      const res = await tenantsApi.getActionSettings(tenantId);
      if (res.data) return res.data as ActionSettingsData;
      // Fallback defaults
      return {
        autoEnrich: true,
        autoPush: false,
        autoBillingActions: false,
        useAdkEnrichment: false,
        alertActionsEnabled: false,
        kountAutoRespond: true,
        alertFallbackAction: 'use_toggles',
        alertRules: [],
      };
    },
    staleTime: 30 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const res = await tenantsApi.updateActionSettings(tenantId, patch);
      return res.data as ActionSettingsData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'action-settings'] });
    },
  });

  const isSaving = updateMutation.isPending;

  function toggle(field: string, currentValue: boolean) {
    updateMutation.mutate({ [field]: !currentValue });
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <CircleNotch size={32} weight="regular" className="text-slate-300 animate-spin mx-auto" />
        <p className="text-sm text-slate-400 mt-3">Loading action settings...</p>
      </div>
    );
  }

  if (isError || !settings) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <WarningCircle size={32} weight="regular" className="text-red-300 mx-auto" />
        <p className="text-sm text-slate-500 mt-3">Failed to load action settings.</p>
      </div>
    );
  }

  const alertRules = settings.alertRules ?? [];

  return (
    <div className="space-y-6">
      {/* Save status indicator */}
      {updateMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <WarningCircle size={18} weight="regular" />
          Failed to save settings. Please try again.
        </div>
      )}

      {/* Chargeback Settings */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Gavel size={20} weight="regular" className="text-primary" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Chargeback Settings</h3>
          {isSaving && (
            <CircleNotch size={16} weight="regular" className="text-slate-400 animate-spin ml-auto" />
          )}
        </div>
        <div className="p-4 space-y-1">
          <AdminToggle
            enabled={settings.autoEnrich}
            onChange={() => toggle('autoEnrich', settings.autoEnrich)}
            label="Auto-Enrich — Automatically enrich new chargebacks"
            disabled={isSaving}
          />
          <AdminToggle
            enabled={settings.autoPush}
            onChange={() => toggle('autoPush', settings.autoPush)}
            label="Auto-Push — Push enriched chargebacks to routed target"
            disabled={isSaving}
          />
          <AdminToggle
            enabled={settings.autoBillingActions}
            onChange={() => toggle('autoBillingActions', settings.autoBillingActions)}
            label="Auto-Billing Actions — Execute billing cleanup after enrichment"
            disabled={isSaving}
          />
          {/* ADK Enrichment toggle removed — all enrichment routes through TS pipeline now */}
        </div>
      </div>

      {/* SLA Configuration */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Timer size={20} weight="regular" className="text-primary" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">SLA Configuration</h3>
          <span className="text-xs text-slate-400 ml-2">Controls the dashboard's SLA tile threshold and grace window</span>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 px-3 py-2">
            <Crosshair size={20} weight="regular" className="text-slate-400" />
            <div className="flex-1">
              <label className="text-sm text-slate-700 block mb-1">SLA Target (%)</label>
              <p className="text-xs text-slate-400 mb-2">Dashboard SLA card turns green at or above this number, amber within 15pp, red below.</p>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={settings.sla?.targetPct ?? 90}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                  updateMutation.mutate({ sla: { ...settings.sla, targetPct: v } });
                }}
                disabled={isSaving}
                className="w-32 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-3 py-2">
            <Clock size={20} weight="regular" className="text-slate-400" />
            <div className="flex-1">
              <label className="text-sm text-slate-700 block mb-1">Grace Window (hours past replyByDate)</label>
              <p className="text-xs text-slate-400 mb-2">Cases submitted within this many hours after the deadline still count as on-time. Set 0 for strict deadline.</p>
              <input
                type="number"
                min={0}
                max={168}
                step={1}
                value={settings.sla?.graceHours ?? 0}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(168, parseInt(e.target.value, 10) || 0));
                  updateMutation.mutate({ sla: { ...settings.sla, graceHours: v } });
                }}
                disabled={isSaving}
                className="w-32 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Alert Settings */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <BellRinging size={20} weight="regular" className="text-primary" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Alert Settings</h3>
        </div>
        <div className="p-4 space-y-1">
          <AdminToggle
            enabled={settings.alertActionsEnabled}
            onChange={() => toggle('alertActionsEnabled', settings.alertActionsEnabled)}
            label="Alert Actions Enabled — Execute billing actions on alert cases"
            disabled={isSaving}
          />
          <AdminToggle
            enabled={settings.kountAutoRespond !== false}
            onChange={() => toggle('kountAutoRespond', settings.kountAutoRespond !== false)}
            label="Kount Auto-Respond — Automatically respond to Kount DCM alerts"
            disabled={isSaving}
          />

          {/* Fallback action dropdown */}
          <div className="flex items-center gap-3 px-3 py-2">
            <ArrowsSplit size={20} weight="regular" className="text-slate-400" />
            <div className="flex-1">
              <label className="text-sm text-slate-700 block mb-1">Fallback Action (when no alert rule matches)</label>
              <select
                value={settings.alertFallbackAction ?? 'use_toggles'}
                onChange={(e) => updateMutation.mutate({ alertFallbackAction: e.target.value })}
                disabled={isSaving}
                className="w-full max-w-xs text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {FALLBACK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Field Profiles */}
      <FieldProfilesSection
        settings={settings}
        onSave={(patch) => updateMutation.mutate(patch)}
        isSaving={isSaving}
        tenantId={tenantId}
      />

      {/* Alert Rules */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks size={20} weight="regular" className="text-primary" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Alert Rules</h3>
            <span className="text-xs text-slate-400 ml-2">
              {alertRules.length} rule{alertRules.length !== 1 ? 's' : ''}
            </span>
          </div>
          <span className="text-xs text-slate-400 italic">Read-only — full CRUD coming soon</span>
        </div>

        {alertRules.length === 0 ? (
          <div className="p-10 text-center">
            <ListPlus size={32} weight="regular" className="text-slate-300 mx-auto" />
            <p className="text-sm text-slate-400 mt-3">
              No alert rules configured. Fallback action will be used for all alerts.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {alertRules
              .sort((a, b) => a.priority - b.priority)
              .map((rule) => {
                const actionBadge = ACTION_TYPE_BADGES[rule.action.type] ?? {
                  label: rule.action.type,
                  color: 'bg-slate-50 text-slate-600 border-slate-200',
                };
                const conditionParts: string[] = [];
                if (rule.conditions.alertSources?.length) {
                  conditionParts.push(`Source: ${rule.conditions.alertSources.join(', ')}`);
                }
                if (rule.conditions.eventTypes?.length) {
                  conditionParts.push(`Events: ${rule.conditions.eventTypes.join(', ')}`);
                }
                if (rule.conditions.amountMin != null || rule.conditions.amountMax != null) {
                  const min = rule.conditions.amountMin != null ? `$${rule.conditions.amountMin}` : '*';
                  const max = rule.conditions.amountMax != null ? `$${rule.conditions.amountMax}` : '*';
                  conditionParts.push(`Amount: ${min} - ${max}`);
                }
                if (rule.conditions.reasonCodes?.length) {
                  conditionParts.push(`Reason: ${rule.conditions.reasonCodes.join(', ')}`);
                }
                if (rule.conditions.currencies?.length) {
                  conditionParts.push(`Currency: ${rule.conditions.currencies.join(', ')}`);
                }

                return (
                  <div key={rule.id} className="px-6 py-4 flex items-center gap-4">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span
                        className={cn(
                          'w-2 h-2 rounded-full flex-shrink-0',
                          rule.enabled ? 'bg-green-500' : 'bg-slate-300',
                        )}
                      />
                      <span className="text-xs text-slate-400 font-mono w-6 text-right flex-shrink-0">
                        #{rule.priority}
                      </span>
                      <span className="text-sm font-medium text-slate-700 truncate">{rule.name}</span>
                    </div>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border flex-shrink-0',
                        actionBadge.color,
                      )}
                    >
                      {actionBadge.label}
                    </span>
                    <span className="text-xs text-slate-400 truncate max-w-[300px] flex-shrink-0">
                      {conditionParts.length > 0 ? conditionParts.join(' | ') : 'Catch-all'}
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Webhook Endpoints + Midigator webhook auth + subscription sync
// ---------------------------------------------------------------------------

const WEBHOOK_INGESTION_BASE = 'https://webhook-ingestion-184100281849.us-central1.run.app';

const URL_KIND_LABEL: Record<MidigatorUrlKind, { label: string; className: string }> = {
  authenticated: { label: 'Authenticated', className: 'text-emerald-600' },
  stale_token: { label: 'Old token', className: 'text-amber-600' },
  legacy: { label: 'Legacy URL', className: 'text-red-600' },
  foreign: { label: 'Other host', className: 'text-red-600' },
};

function formatUnix(seconds: number | null): string {
  return seconds ? new Date(seconds * 1000).toLocaleString() : '—';
}

/**
 * Midigator does not sign webhooks, so its URL carries a per-tenant secret
 * token (docs/superpowers/specs/2026-10-15-midigator-webhook-auth-design.md).
 *
 * Every call goes to dispute-processor's staff-only admin API (same origin
 * policy as the rest of this portal). The token is WRITE-ONLY: a masked URL and
 * NO copy button. The Require-token button is locked by `requireTokenLock`,
 * which fails CLOSED, and the server re-checks on PATCH.
 */
function MidigatorWebhookPanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ['midigator-auth', tenantId],
    queryFn: () => midigatorAuthApi.getStatus(tenantId),
    retry: false,
  });
  const status = statusQuery.data;

  const subsQuery = useQuery({
    queryKey: ['midigator-subs', tenantId],
    queryFn: () => midigatorAuthApi.getSubscriptions(tenantId),
    enabled: Boolean(status?.configured),
    retry: false,
  });
  const subs = subsQuery.data?.subscriptions ?? [];
  const foreignSubs = subs.filter((s) => s.urlKind === 'foreign');

  const lock = requireTokenLock({
    statusOk: statusQuery.isSuccess,
    hasToken: Boolean(status?.hasToken),
    subscriptionsQueryOk: subsQuery.isSuccess && Boolean(subsQuery.data),
    subscriptions: subsQuery.data,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['midigator-auth', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['midigator-subs', tenantId] });
  };

  const tokenMutation = useMutation({
    mutationFn: (force: boolean) => midigatorAuthApi.generateToken(tenantId, { force }),
    onSuccess: refresh,
  });
  const requireMutation = useMutation({
    mutationFn: (requireToken: boolean) => midigatorAuthApi.setRequireToken(tenantId, requireToken),
    onSuccess: refresh,
  });
  const syncMutation = useMutation({
    mutationFn: () => midigatorAuthApi.sync(tenantId),
    onSuccess: refresh,
  });
  const deleteMutation = useMutation({
    mutationFn: (guid: string) => midigatorAuthApi.deleteSubscription(tenantId, guid),
    onSuccess: refresh,
  });

  const onGenerate = () => {
    if (!status?.hasToken) {
      tokenMutation.mutate(false);
      return;
    }
    if (status.hasPreviousToken) {
      if (!window.confirm(
        `An earlier token is still live until ${formatUnix(status.previousExpiresAt)}.\n\n`
        + 'Rotating now DISCARDS it with no overlap — any subscription still on it stops delivering. '
        + 'Only force this if the panel shows no "Old token" subscriptions.\n\nForce rotate?',
      )) return;
      tokenMutation.mutate(true);
      return;
    }
    if (!window.confirm(
      'Rotate the Midigator webhook token?\n\n'
      + 'The current token becomes the "previous" token and keeps working for 7 days. '
      + 'Midigator keeps posting to it until you run "Sync subscriptions".',
    )) return;
    tokenMutation.mutate(false);
  };

  const onRequire = (requireToken: boolean) => {
    const msg = requireToken
      ? 'Require the token?\n\nThe legacy URL will REJECT every delivery from now on. '
        + 'The server re-checks live subscriptions and the last 2 hours of receipts first.'
      : 'Allow the legacy unauthenticated URL again?\n\nThis re-opens the hole for this tenant. Use only to roll back a cutover.';
    if (window.confirm(msg)) requireMutation.mutate(requireToken);
  };

  const onDelete = (guid: string, what: string) => {
    if (window.confirm(`Delete Midigator subscription ${guid} (${what})?\n\nThis is a Midigator API call and cannot be undone.`)) {
      deleteMutation.mutate(guid);
    }
  };

  const mutationError = [tokenMutation, requireMutation, syncMutation, deleteMutation]
    .find((m) => m.isError)?.error as Error | undefined;

  return (
    <div className="mt-1 space-y-2">
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs font-mono text-slate-600 truncate">
          {status?.maskedWebhookUrl ?? 'No webhook token yet — generate one below'}
        </code>
        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-400" title="The token is write-only; Sync registers the URL with Midigator.">
          <Lock size={12} /> Write-only
        </span>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2">
        {statusQuery.isLoading ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : statusQuery.isError ? (
          <p className="text-xs text-red-600">Could not load Midigator webhook auth status</p>
        ) : !status?.configured ? (
          <p className="text-xs text-slate-400 italic">No Midigator credentials configured for this tenant</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Token</span>
              <span className="font-mono text-slate-700">{status.hasToken ? status.tokenPreview : 'none'}</span>
              {status.hasToken && <span className="text-slate-400"> · {formatUnix(status.tokenCreatedAt)}</span>}
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Previous token</span>
              <span className="text-slate-700">
                {status.hasPreviousToken ? `valid until ${formatUnix(status.previousExpiresAt)}` : 'none'}
              </span>
            </div>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Legacy URL</span>
              <span className={cn('font-semibold', status.requireToken ? 'text-emerald-600' : 'text-amber-600')}>
                {status.requireToken ? 'Rejected (token required)' : 'Accepted — transition'}
              </span>
              {status.requireTokenExplicit === null && <span className="text-slate-400"> · default</span>}
            </div>
          </div>
        )}

        {status?.configured && (
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Event Subscriptions (all hosts)</span>
            {subsQuery.isLoading ? (
              <p className="text-xs text-slate-400">Loading…</p>
            ) : subsQuery.isError || !subsQuery.data?.subscriptionsOk ? (
              <p className="text-xs text-red-600">
                Could not load subscriptions from Midigator — the list is UNKNOWN, not empty. Require token stays locked.
              </p>
            ) : subs.length > 0 ? (
              <div className="mt-1 space-y-1">
                {subs.map((sub) => {
                  const kind = URL_KIND_LABEL[sub.urlKind];
                  return (
                    <div key={sub.guid} className="flex items-center gap-2 text-xs">
                      <span className={cn('w-2 h-2 rounded-full shrink-0', sub.active ? 'bg-emerald-500' : 'bg-amber-400')} />
                      <span className="font-mono text-slate-700">{sub.event_type}</span>
                      <span className={cn('text-[10px] font-bold uppercase', sub.active ? 'text-emerald-600' : 'text-amber-600')}>
                        {sub.active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={cn('text-[10px] font-bold uppercase', kind.className)}>{kind.label}</span>
                      <span className="font-mono text-[10px] text-slate-400 truncate">{sub.host}</span>
                      {sub.urlKind !== 'authenticated' && (
                        <button
                          onClick={() => onDelete(sub.guid, `${sub.event_type} on ${sub.host}`)}
                          disabled={deleteMutation.isPending}
                          className="ml-auto text-[10px] font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Midigator reports no subscriptions</p>
            )}
            {foreignSubs.length > 0 && (
              <p className="mt-1 text-xs text-red-700">
                {foreignSubs.length} subscription(s) post to a host that is NOT this service
                ({[...new Set(foreignSubs.map((s) => s.host))].join(', ')}). Confirm what each host is before syncing:
                Sync refuses to create a subscription beside one, and Require token stays locked while any exists.
              </p>
            )}
          </div>
        )}

        {status?.configured && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={onGenerate}
              disabled={tokenMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
            >
              <ArrowsClockwise size={14} />
              {status.hasToken ? 'Rotate token' : 'Generate webhook token'}
            </button>
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || !status.hasToken}
              title={status.hasToken ? undefined : 'Generate a token first'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50 transition"
            >
              <BellRinging size={14} />
              {syncMutation.isPending ? 'Syncing… (re-checks activation after ~30 s)' : 'Sync subscriptions to authenticated URL'}
            </button>
            {status.requireToken ? (
              <button
                onClick={() => onRequire(false)}
                disabled={requireMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 transition"
              >
                Allow legacy URL (rollback)
              </button>
            ) : (
              <button
                onClick={() => onRequire(true)}
                disabled={requireMutation.isPending || !lock.enabled}
                title={lock.enabled ? undefined : lock.reason}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition"
              >
                <Lock size={14} />
                Require token
              </button>
            )}
          </div>
        )}
        {status?.configured && !status.requireToken && !lock.enabled && (
          <p className="text-[10px] text-slate-400">Require token locked: {lock.reason}</p>
        )}

        {syncMutation.data && (
          <div className="text-xs text-slate-600 space-y-0.5">
            <p>
              Sync: {syncMutation.data.summary.succeeded}/{syncMutation.data.summary.total} calls ok ·{' '}
              <span className={syncMutation.data.summary.activeAfterRecheck === syncMutation.data.summary.total ? 'text-emerald-700' : 'text-red-700'}>
                {syncMutation.data.summary.activeAfterRecheck}/{syncMutation.data.summary.total} active on the authenticated URL
                {' '}after {Math.round(syncMutation.data.recheckDelayMs / 1000)} s
              </span>
            </p>
            {syncMutation.data.results.filter((r) => !r.success || r.activeAfterRecheck !== true).map((r) => (
              <p key={`${r.event_type}-${r.guid ?? 'new'}`} className="text-red-600">
                {r.event_type} ({r.action}): {r.error ?? (r.activeAfterRecheck === false ? 'NOT active after re-check' : 'could not re-check')}
              </p>
            ))}
            {syncMutation.data.redundant.length > 0 && (
              <p className="text-amber-700">
                {syncMutation.data.redundant.length} redundant subscription(s) would double-deliver — delete them above:{' '}
                <span className="font-mono">{syncMutation.data.redundant.map((r) => r.guid).join(', ')}</span>
              </p>
            )}
          </div>
        )}

        {mutationError && <p className="text-xs text-red-600">{mutationError.message}</p>}
      </div>
    </div>
  );
}

function WebhookEndpoints({ tenantId }: { tenantId: string }) {
  // Kount and Justt authenticate by signature, so their URLs are not secrets.
  // Midigator's IS one — it is rendered by MidigatorWebhookPanel, masked.
  const webhookPlatforms = [
    { label: 'Kount DCM', path: 'kount', desc: 'Verifi CDRN + Ethoca fraud alerts' },
    { label: 'Justt', path: 'justt', desc: 'Dispute management webhooks' },
    { label: 'Midigator', path: 'midigator', desc: 'Chargeback event webhooks — URL carries a secret token' },
  ];

  return (
    <div className="px-5 py-3 space-y-4">
      {webhookPlatforms.map((wp) => {
        const url = `${WEBHOOK_INGESTION_BASE}/webhooks/${wp.path}/${tenantId}`;
        return (
          <div key={wp.path}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-800">{wp.label}</span>
              <span className="text-xs text-slate-400">{wp.desc}</span>
            </div>
            {wp.path === 'midigator' ? (
              <MidigatorWebhookPanel tenantId={tenantId} />
            ) : (
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs font-mono text-slate-600 truncate select-all">
                  {url}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(url)}
                  className="shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/_authenticated/tenants/$tenantId' });
  const [activeTab, setActiveTab] = useState<Tab>('sync_history');
  const { data: tenants = [], isError: tenantsError } = useTenants();
  const { data: syncRuns = [] } = useSyncRuns(20);
  const {
    data: entitlement,
    isLoading: entitlementLoading,
    isError: entitlementError,
    error: entitlementErr,
  } = useTenantEntitlement(tenantId);

  const tenantFromList = tenants.find((t) => t.id === tenantId);

  // "Not found" only when the API itself answered 404. The list falls back to
  // mock tenants when it cannot reach the admin API, so a tenant created two
  // seconds ago is legitimately absent from it — and telling an operator their
  // brand new tenant does not exist is how a duplicate gets created under a
  // second, permanent id.
  const confirmedMissing =
    !tenantFromList && entitlementError && apiErrorParts(entitlementErr).status === 404;

  if (confirmedMissing) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <MagnifyingGlass size={40} weight="regular" className="text-slate-300 mb-4" />
        <h3 className="text-lg font-bold text-slate-700">Tenant Not Found</h3>
        <p className="text-sm text-slate-400 mt-1">No tenant with ID "{tenantId}" exists.</p>
        <Link to="/tenants" className="mt-4 text-sm text-primary font-medium hover:underline">
          Back to Tenants
        </Link>
      </div>
    );
  }

  const tenantName = entitlement?.name ?? tenantFromList?.name ?? tenantId;
  const platforms = Object.keys(PLATFORM_BADGES).slice(0, tenantFromList?.activeSources ?? 0);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'access', label: 'Access & Users' },
    { key: 'credentials', label: 'Credentials' },
    { key: 'brands', label: 'Brands' },
    { key: 'data_preview', label: 'Data Preview' },
    { key: 'sync_history', label: 'Sync History' },
    { key: 'action_settings', label: 'Action Settings' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to="/tenants" className="text-slate-400 hover:text-primary transition">
          Tenants
        </Link>
        <span className="text-slate-300">/</span>
        <span className="text-slate-700 font-medium">{tenantName}</span>
      </div>

      {/* Overview Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-3xl font-bold text-slate-900">{tenantName}</h2>
              {/* Suspension has to be visible without opening a tab. */}
              <TenantAccessChip
                entitlement={entitlement}
                isLoading={entitlementLoading}
                isError={entitlementError}
              />
            </div>
            <p className="text-sm font-mono text-slate-400 mt-1">{tenantId}</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition">
              <ArrowsClockwise size={18} weight="regular" />
              Trigger Sync
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
              <ArrowClockwise size={18} weight="regular" />
              Re-run Normalization
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 mt-6 pt-6 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Warehouse Project ID</p>
            <p className="text-sm font-mono text-slate-700 mt-1">{tenantFromList?.bqProjectId ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Active Platforms</p>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {platforms.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                >
                  {PLATFORM_BADGES[p]}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Environment</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-slate-700 font-medium">Production</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Sync Status</p>
            <div className="mt-1.5">
              {tenantFromList ? (
                <StatusBadge variant={tenantFromList.status} />
              ) : (
                <span className="text-sm text-slate-400">—</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'pb-3 text-sm font-medium border-b-2 transition',
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'access' && (
        <div className="space-y-4">
          <TenantAccessPanel
            tenantId={tenantId}
            tenantName={tenantName}
            entitlement={entitlement}
            isLoading={entitlementLoading}
            isError={entitlementError}
            error={entitlementErr}
          />
          {/* Beside access on purpose: both decide who may reach this tenant's
              data, and the connector is only ever enabled for an active tenant. */}
          <TenantMcpPanel
            tenantId={tenantId}
            tenantName={tenantName}
            entitlement={entitlement}
            isLoading={entitlementLoading}
            isError={entitlementError}
            error={entitlementErr}
          />
          <InviteFirstUserPanel tenantId={tenantId} />
          {/* Below the whole-tenant control on purpose: suspension is the
              heavier, rarer action, and an operator who scrolls past it to
              reach a single person has seen the proportionate option second,
              not instead. */}
          <TenantMembersPanel tenantId={tenantId} />
        </div>
      )}

      {activeTab === 'brands' && (
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <BrandsPanel tenantId={tenantId} />
        </div>
      )}

      {activeTab === 'sync_history' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-6 py-3">Run ID</th>
                <th className="text-left px-6 py-3">Job Type</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Rows Processed</th>
                <th className="text-right px-6 py-3">Duration</th>
                <th className="text-right px-6 py-3">Completion Time</th>
              </tr>
            </thead>
            <tbody>
              {syncRuns.map((run) => (
                <tr key={run.runId} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition">
                  <td className="px-6 py-4 text-sm font-mono text-primary font-medium">{run.runId}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{run.jobType}</td>
                  <td className="px-6 py-4">
                    <StatusBadge variant={run.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">
                    {run.rowsProcessed.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 text-right">{run.duration}</td>
                  <td className="px-6 py-4 text-sm text-slate-500 text-right">{run.completionTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'credentials' && (
        <div className="space-y-4">
          {/* Webhook Endpoints */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
              <WebhooksLogo size={18} weight="regular" className="text-primary" />
              <h3 className="text-sm font-bold text-slate-800">Webhook Endpoints</h3>
              <span className="text-xs text-slate-400 ml-1">Provide to each platform for real-time events</span>
            </div>
            <WebhookEndpoints tenantId={tenantId} />
          </div>

          {/* Credential Placeholder */}
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
            <Lock size={32} weight="regular" className="text-slate-300 mx-auto" />
            <h3 className="text-lg font-bold text-slate-700 mt-3">Credential Management</h3>
            <p className="text-sm text-slate-400 mt-1">Platform credentials stored in Firestore. Management UI coming soon.</p>
          </div>
        </div>
      )}

      {activeTab === 'data_preview' && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <Table size={32} weight="regular" className="text-slate-300 mx-auto" />
          <h3 className="text-lg font-bold text-slate-700 mt-3">Data Preview</h3>
          <p className="text-sm text-slate-400 mt-1">Query BigQuery canonical tables directly. Coming soon.</p>
        </div>
      )}

      {activeTab === 'action_settings' && (
        <div className="space-y-4">
          {/* Above the action toggles: this is the text that ships INSIDE the
              representment package, so an operator configuring how disputes are
              fought reads it before deciding whether to fight them
              automatically. It is not an access concern and does not belong on
              the Access tab, where it was first placed and could not be found. */}
          <EvidenceProfilePanel tenantId={tenantId} />
          <ActionSettingsTab tenantId={tenantId} />
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/tenants/$tenantId')({
  component: TenantDetailPage,
});
